import { Camera, Images, LogOut, MoreHorizontal, UserMinus, UserPlus, Users } from 'lucide-react'
import React, { useState } from 'react'

import { Button } from '@/components/ui/button'

import { useAuth } from '@/contexts/auth-context'

import { AddParticipantsDialog } from './add-participants-dialog'
import { ConversationMediaDialog } from './conversation-media-dialog'
import { LeaveConversationDialog } from './leave-conversation-dialog'
import { RemoveParticipantsDialog } from './remove-participants-dialog'
import { UpdateConversationAvatarDialog } from './update-conversation-avatar-dialog'
import { ViewParticipantsDialog } from './view-participants-dialog'

import { Conversation, ConversationType, ParticipantRole } from '@/types/chat.type'

interface ConversationSettingsButtonProps {
  conversation: Conversation
}

export const ConversationSettingsButton: React.FC<ConversationSettingsButtonProps> = ({ conversation }) => {
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [activeDialog, setActiveDialog] = useState<string | null>(null)

  const currentUserParticipant = conversation?.participants?.find(p => p.user.id === user?.id)
  const isOwner = currentUserParticipant?.role === ParticipantRole.OWNER
  const canManageParticipants = isOwner

  const handleDialogOpen = (dialogType: string) => {
    setActiveDialog(dialogType)
    setIsOpen(false)
  }

  const handleDialogClose = () => {
    setActiveDialog(null)
  }

  // Handle click outside and escape key to close the settings menu
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (isOpen && target && !target.closest('.conversation-settings-button')) {
        setIsOpen(false)
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen])

  // Don't render if conversation or participants are not available
  if (!conversation || !conversation.participants) {
    return null
  }

  return (
    <>
      <div className="relative conversation-settings-button">
        <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)} className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>

        {/* View Participants - Available to all */}
        {isOpen && (
          <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-[10002]">
            <div className="py-1">
              <button
                onClick={() => handleDialogOpen('view')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <Users className="h-4 w-4" />
                View Participants
              </button>

              <button
                onClick={() => handleDialogOpen('media')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <Images className="h-4 w-4" />
                View Media
              </button>

              {/* Upload Avatar - Owner only for GROUP, COURSE, and COMMUNITY */}
              {canManageParticipants &&
                (conversation.type === ConversationType.GROUP ||
                  conversation.type === ConversationType.COURSE ||
                  conversation.type === ConversationType.COMMUNITY) && (
                  <button
                    onClick={() => handleDialogOpen('avatar')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Camera className="h-4 w-4" />
                    Upload Avatar
                  </button>
                )}

              {/* Add Participants - Owner only for GROUP, COURSE, and COMMUNITY */}
              {canManageParticipants &&
                (conversation.type === ConversationType.GROUP ||
                  conversation.type === ConversationType.COURSE ||
                  conversation.type === ConversationType.COMMUNITY) && (
                  <button
                    onClick={() => handleDialogOpen('add')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Add Participants
                  </button>
                )}

              {/* Remove Participants - Owner only for GROUP, COURSE, and COMMUNITY */}
              {canManageParticipants &&
                (conversation.type === ConversationType.GROUP ||
                  conversation.type === ConversationType.COURSE ||
                  conversation.type === ConversationType.COMMUNITY) && (
                  <button
                    onClick={() => handleDialogOpen('remove')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <UserMinus className="h-4 w-4" />
                    Remove Participants
                  </button>
                )}

              {/* Leave Conversation - Not owner for GROUP, COURSE, and COMMUNITY */}
              {!isOwner &&
                (conversation.type === ConversationType.GROUP ||
                  conversation.type === ConversationType.COURSE ||
                  conversation.type === ConversationType.COMMUNITY) && (
                  <button
                    onClick={() => handleDialogOpen('leave')}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Leave Conversation
                  </button>
                )}
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      {activeDialog === 'view' && <ViewParticipantsDialog conversation={conversation} onClose={handleDialogClose} />}

      {activeDialog === 'media' && <ConversationMediaDialog conversation={conversation} onClose={handleDialogClose} />}

      {activeDialog === 'avatar' && (
        <UpdateConversationAvatarDialog conversation={conversation} isOpen={true} onClose={handleDialogClose} />
      )}

      {activeDialog === 'add' && <AddParticipantsDialog conversation={conversation} onClose={handleDialogClose} />}

      {activeDialog === 'remove' && (
        <RemoveParticipantsDialog conversation={conversation} onClose={handleDialogClose} />
      )}

      {activeDialog === 'leave' && <LeaveConversationDialog conversation={conversation} onClose={handleDialogClose} />}
    </>
  )
}
